import { useEffect, useMemo, useState } from 'react';
import { IoMdArrowBack } from 'react-icons/io';
import { IoSearchOutline } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import SkeletonOne from '../components/skeleton/SkeletonOne';
import { getAndCreateChatRoom } from '../redux/ApiSlice/MessagesSlice';
import { getMutualFriendsList } from '../redux/ApiSlice/MutualFriendsSlice';

export default function MutualFriends() {
    const { userId } = useParams();
    const { data, loading, error } = useSelector((state) => state.mutualFriends);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getMutualFriendsList(userId));
    }, [dispatch, userId]);

    const filteredList = useMemo(() => {
        if (!data) return [];
        if (searchTerm.trim() === "") return data;
        const search = searchTerm.toLowerCase();
        return data.filter((item) => {
            const username = item?.username?.toLowerCase() || "";
            const fullName = item?.full_name?.toLowerCase() || "";
            return username.includes(search) || fullName.includes(search);
        });
    }, [searchTerm, data]);

    const handleStartChat = async (id) => {
        try {
            const response = await dispatch(getAndCreateChatRoom(id));
            if (response?.payload?.id) {
                navigate(`/message/${response.payload.id}`);
            }
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <section className="h-full bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0 z-50">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate(-1)}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black dark:text-black">
                        Mutual Friends
                    </p>
                </div>
            </div>

            <div className="relative m-2">
                <IoSearchOutline className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[18px] text-black" />
                <input
                    type="text"
                    value={searchTerm}
                    placeholder="Find Your Mutual Friends"
                    onChange={e => setSearchTerm(e.target.value)}
                    className="w-full text-[14px] pl-8 pr-2 py-1.5 rounded-lg bg-gray-100 dark:bg-[#333333] dark:text-white outline-none focus:ring-2 focus:ring-[#BDE6FF]"
                />
            </div>
            {loading && (
                <SkeletonOne />
            )}

            {filteredList.length > 0 && !loading ? (
                <div className="h-[90%] pb-10 overflow-y-auto no-scrollbar">
                    {filteredList.map((item) => (
                        <div className="flex items-center py-2 border-b border-gray-300 dark:border-gray-800/50" key={item.id}>
                            <div className="grid grid-cols-12 justify-between w-full px-2">
                                <div className="col-span-7 flex items-start gap-x-1.5 rounded-full">
                                    <img
                                        src={item?.avatar_url}
                                        alt={item?.full_name || item?.username}
                                        onClick={() => navigate(`/account/${item?.id}`)}
                                        className="h-10 w-10 rounded-full object-cover cursor-pointer border border-gray-300 dark:border-gray-800/50 bg-gray-100 dark:bg-[#333333]"
                                    />
                                    <div className="flex flex-col h-full justify-center w-full">
                                        <p
                                            onClick={() => navigate(`/account/${item?.id}`)}
                                            className="text-[11px] first-letter:uppercase whitespace-nowrap font-bold dark:text-white text-gray-600"
                                        >
                                            {item?.full_name}
                                        </p>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                            @{item?.username}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-span-5 flex items-center justify-end gap-x-1">
                                    <button
                                        onClick={() => handleStartChat(item?.id)}
                                        className="w-[4.2rem] bg-gray-800 hover:bg-gray-900 text-white text-[10px] font-semibold py-1 rounded-md whitespace-nowrap"
                                    >
                                        Message
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex justify-center items-center h-[50%] w-full">
                    {!loading && <p className="text-[10px] text-gray-400 dark:text-gray-200 mt-5">
                        {searchTerm ? 'No result found' : 'No mutual friends found'}
                    </p>}
                </div>
            )}
        </section>
    )
}
